import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { useChat } from '../../../hooks/useChat';
import toast from 'react-hot-toast';

const BUILT_IN_TEMPLATES = [
  { id: 'bt1', name: 'Welcome Email', type: 'email', desc: 'Greet new customers after first service', content: 'Hi [First Name],\n\nThank you for choosing us! We are thrilled to have you as a customer.\n\nBest,\n[Business Name]' },
  { id: 'bt2', name: 'Follow-up After Job', type: 'email', desc: 'Request review after completed service', content: 'Hi [First Name],\n\nWe hope your recent service met your expectations. We would love your feedback!\n\n[Review Link]\n\nBest,\n[Business Name]' },
  { id: 'bt3', name: 'Seasonal Promotion', type: 'email', desc: 'Seasonal discount offer', content: 'Hi [First Name],\n\nSpecial offer this season! Get [X]% off your next service.\n\nBook now: [CTA]\n\nBest,\n[Business Name]' },
  { id: 'bt4', name: 'Appointment Reminder', type: 'sms', desc: 'SMS reminder before appointment', content: 'Hi [Name], reminder: your appointment with [Business] is tomorrow at [Time]. Reply CONFIRM or call us.' },
  { id: 'bt5', name: 'Referral Request', type: 'email', desc: 'Ask satisfied customers for referrals', content: 'Hi [First Name],\n\nWe love working with you! If you know anyone who needs our services, we offer a referral bonus.\n\nThank you,\n[Business Name]' },
  { id: 'bt6', name: 'Re-engagement', type: 'email', desc: 'Win back inactive customers', content: 'Hi [First Name],\n\nWe miss you! It has been a while since your last service. Come back and get [X]% off.\n\nBook now: [CTA]' },
];

const TemplatesTab = ({ onUseTemplate }) => {
  const { profile } = useAuth();
  const [orgTemplates, setOrgTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerator, setShowGenerator] = useState(false);
  const [genPrompt, setGenPrompt] = useState('');
  const [genName, setGenName] = useState('');
  const [generating, setGenerating] = useState(false);

  const { sendMessage, response, isLoading } = useChat('ANTHROPIC', 'claude-sonnet-4-5-20250929', false);
  const [aiAction, setAiAction] = useState(null);
  const [generatedContent, setGeneratedContent] = useState({ email: '', sms: '' });

  const fetchTemplates = useCallback(async () => {
    if (!profile?.org_id) return;
    const { data } = await supabase?.from('templates')?.select('*')?.eq('org_id', profile?.org_id)?.in('template_type', ['email', 'sms'])?.is('deleted_at', null)?.order('created_at', { ascending: false });
    setOrgTemplates(data || []);
    setLoading(false);
  }, [profile?.org_id]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  useEffect(() => {
    if (response && !isLoading && generating && aiAction) {
      if (aiAction === 'email') {
        setGeneratedContent((prev) => ({ ...prev, email: response }));
        setAiAction('sms');
        sendMessage([
          { role: 'system', content: 'Write a concise SMS version (under 160 chars) of this email campaign.' },
          { role: 'user', content: response },
        ]);
      } else if (aiAction === 'sms') {
        setGeneratedContent((prev) => ({ ...prev, sms: response?.slice(0, 160) }));
        setGenerating(false);
        setAiAction(null);
      }
    }
  }, [response, isLoading]);

  const handleGenerate = () => {
    if (!genPrompt?.trim()) { toast?.error('Describe your template'); return; }
    setGenerating(true);
    setAiAction('email');
    setGeneratedContent({ email: '', sms: '' });
    sendMessage([
      { role: 'system', content: 'You are an email marketing copywriter for home service businesses. Write a professional email marketing template based on the description. Include placeholders like [First Name], [Business Name], [CTA].' },
      { role: 'user', content: genPrompt },
    ]);
  };

  const handleSaveGenerated = async () => {
    if (!generatedContent?.email && !generatedContent?.sms) return;
    const toInsert = [];
    if (generatedContent?.email) toInsert?.push({ org_id: profile?.org_id, name: genName || genPrompt?.slice(0, 40), template_type: 'email', content: generatedContent?.email });
    if (generatedContent?.sms) toInsert?.push({ org_id: profile?.org_id, name: (genName || genPrompt?.slice(0, 40)) + ' (SMS)', template_type: 'sms', content: generatedContent?.sms });
    const { error } = await supabase?.from('templates')?.insert(toInsert);
    if (!error) { toast?.success('Templates saved'); fetchTemplates(); setShowGenerator(false); setGenPrompt(''); setGenName(''); setGeneratedContent({ email: '', sms: '' }); }
    else toast?.error('Failed to save');
  };

  const allTemplates = [
    ...BUILT_IN_TEMPLATES,
    ...orgTemplates?.map((t) => ({ ...t, isOrg: true })),
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-foreground">Templates</h2>
        <button onClick={() => setShowGenerator(!showGenerator)}
          className="flex items-center space-x-1.5 px-3 py-2 bg-violet-600 text-white rounded-xl text-sm font-medium hover:bg-violet-700 transition-opacity">
          <Icon name="Sparkles" size={16} />
          <span>Generate with AI</span>
        </button>
      </div>
      {/* AI Generator */}
      {showGenerator && (
        <div className="p-4 bg-violet-50 border border-violet-200 rounded-xl space-y-3">
          <h3 className="text-sm font-semibold text-violet-800">AI Template Generator</h3>
          <input type="text" value={genName} onChange={(e) => setGenName(e?.target?.value)}
            placeholder="Template name (optional)"
            className="w-full px-3 py-2 border border-violet-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-violet-400" />
          <textarea value={genPrompt} onChange={(e) => setGenPrompt(e?.target?.value)}
            placeholder="Describe your template (e.g. 'Follow-up email after HVAC installation asking for a Google review')"
            rows={3}
            className="w-full px-3 py-2 border border-violet-200 rounded-lg bg-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-violet-400" />
          <button onClick={handleGenerate} disabled={generating || isLoading}
            className="w-full py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-60">
            {generating ? 'Generating...' : 'Generate Email + SMS'}
          </button>
          {(generatedContent?.email || generatedContent?.sms) && (
            <div className="space-y-2">
              {generatedContent?.email && (
                <div className="p-3 bg-white border border-violet-200 rounded-lg">
                  <p className="text-xs font-semibold text-violet-700 mb-1">Email</p>
                  <p className="text-xs text-foreground whitespace-pre-line">{generatedContent?.email}</p>
                </div>
              )}
              {generatedContent?.sms && (
                <div className="p-3 bg-white border border-violet-200 rounded-lg">
                  <p className="text-xs font-semibold text-violet-700 mb-1">SMS ({generatedContent?.sms?.length}/160)</p>
                  <p className="text-xs text-foreground">{generatedContent?.sms}</p>
                </div>
              )}
              <button onClick={handleSaveGenerated}
                className="w-full py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700">
                Save Templates
              </button>
            </div>
          )}
        </div>
      )}
      {/* Template Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {allTemplates?.map((tmpl) => (
          <div key={tmpl?.id} className="bg-background border border-border rounded-xl p-4 flex flex-col">
            <div className="flex items-start justify-between mb-2">
              <div>
                <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                  tmpl?.type === 'email' || tmpl?.template_type === 'email' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                }`}>{tmpl?.type || tmpl?.template_type}</span>
              </div>
              {tmpl?.isOrg && (
                <span className="text-xs text-primary font-medium">Custom</span>
              )}
            </div>
            <h3 className="text-sm font-semibold text-foreground mb-1">{tmpl?.name}</h3>
            <p className="text-xs text-muted-foreground flex-1 mb-3">{tmpl?.desc || tmpl?.content?.slice(0, 80) + '...'}</p>
            <button
              onClick={() => onUseTemplate?.(tmpl)}
              className="w-full py-2 border border-primary text-primary rounded-lg text-xs font-medium hover:bg-primary/5 transition-colors"
            >
              Use Template
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TemplatesTab;
